/* eslint-disable max-len */
import { ApiResponseModel } from '../types/ApiResponseModel';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { DyslexiaScreenerContactModel } from '../types/DyslexiaScreenerContactModel';
import { DyslexiaScreenerFileListItemModel } from '../types/DyslexiaScreenerFileListItemModel';
import { DyslexiaScreenerFormViewModel } from '../types/DyslexiaScreenerFormViewModel';
import { DyslexiaScreenerListResponseModel } from '../types/DyslexiaScreenerListResponseModel';
import { DyslexiaScreenerModel } from '../types/DyslexiaScreenerModel';
import { SelectOption } from '../types/SelectOption';
import {
  dyslexiaScreenerNewStaffTrainingUiRef,
  DYSLEXIA_SCREENER_ANNUAL_ACKNOWLEDGEMENT_TEXT,
  isTrainingAcknowledgementDatePopulated,
  returnDyslexiaScreenerTrainingYearOrdinalInBlock,
  shouldShowDyslexiaScreenerTrainingDetailFields,
} from '../utilities/dyslexiaScreenerUtilities';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

const schoolYearPathValueToApiInt = (
  schoolYearFromRouteOrDropdown: string
): number => {
  const matches = schoolYearFromRouteOrDropdown.match(/\d{4}/g);
  if (matches && matches.length > 0) {
    return parseInt(matches[0], 10);
  }
  const n = parseInt(schoolYearFromRouteOrDropdown, 10);
  return Number.isNaN(n) ? 0 : n;
};

export const dyslexiaScreenerApiPaths = {
  create: 'dyslexiascreener',
  update: 'dyslexiascreener',
  getForm: (schoolYearFromRouteOrDropdown?: string) => {
    const parsedSchoolYear = schoolYearPathValueToApiInt(
      schoolYearFromRouteOrDropdown || ''
    );
    return parsedSchoolYear > 0
      ? `dyslexiascreener/form?schoolYear=${parsedSchoolYear}`
      : 'dyslexiascreener/form';
  },
  getListItems: (schoolYearFromRouteOrDropdown: string) =>
    `dyslexiascreener?schoolYear=${schoolYearPathValueToApiInt(
      schoolYearFromRouteOrDropdown
    )}`,
  getById: (id: string) => `dyslexiascreener/${encodeURIComponent(id)}`,
  getContact: 'dyslexiascreenercontact',
  createContact: 'dyslexiascreenercontact',
  updateContact: 'dyslexiascreenercontact',
  getSchoolYears:
    'common/schoolyearsbymodule/formview/DyslexiaScreener?showLongYears=true',
  getLeas: '/common/lealistforcurrentuser/formview?dataDomain=OtherPermissions',
  getSchoolsByLeaId: (leaId: string): string =>
    `/common/schoollistforlea/formview/${encodeURIComponent(
      leaId
    )}?dataDomain=OtherPermissions`,
  uploadFile: (
    schoolYear: string,
    schoolIdentifier: string,
    leaIdentifier: string,
    documentType: string
  ): string =>
    `file/dyslexiascreener?schoolYear=${encodeURIComponent(
      schoolYear
    )}&schoolIdentifier=${encodeURIComponent(
      schoolIdentifier
    )}&leaIdentifier=${encodeURIComponent(
      leaIdentifier
    )}&documentType=${encodeURIComponent(documentType)}`,
  deleteFile: (leaId: string, id: string): string =>
    `file/dyslexiascreener/${encodeURIComponent(leaId)}/${encodeURIComponent(
      id
    )}`,
  getFilesBySchool: (
    schoolYear: string,
    leaId: string,
    schoolIdentifier: string
  ): string =>
    `file/dyslexiascreener/${encodeURIComponent(
      schoolYear
    )}/${encodeURIComponent(leaId)}/${encodeURIComponent(schoolIdentifier)}`,
  downloadFile: (leaId: string, metaId: string): string =>
    `file/dyslexiascreener/download/${encodeURIComponent(
      leaId
    )}/${encodeURIComponent(metaId)}`,
};

export const DyslexiaScreenerServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const getDyslexiaScreenerById = async (
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<DyslexiaScreenerModel>> => {
  return executeApiRequest<DyslexiaScreenerModel>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getById(id)
    );
  });
};

export const getDyslexiaScreenerFormViewModel = async (
  schoolYearFromRouteOrDropdown?: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<DyslexiaScreenerFormViewModel>> => {
  return executeApiRequest<DyslexiaScreenerFormViewModel>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getForm(schoolYearFromRouteOrDropdown)
    );
  });
};

const stripClientOnlyDyslexiaScreenerFields = (
  body: DyslexiaScreenerModel
): Omit<
  DyslexiaScreenerModel,
  | 'grade1FailedOrAtRiskPercentage'
  | 'kgFailedOrAtRiskPercentage'
  | 'kgExists'
  | 'grade1Exists'
  | 'grade1IEPStudentCount'
  | 'grade1504StudentCount'
  | 'kgIEPStudentCount'
  | 'kg504StudentCount'
> => {
  const payload = { ...body } as Record<string, unknown>;
  const persistTrainingDetails = shouldShowDyslexiaScreenerTrainingDetailFields(
    body.schoolYear,
    body.trainingAcknowledgementDate ?? '',
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired
  );

  if (!persistTrainingDetails) {
    payload.trainingUsed = '';
    payload.otherTrainingUsed = '';
  }

  const trainingAckDateSet = isTrainingAcknowledgementDatePopulated(
    body.trainingAcknowledgementDate ?? ''
  );
  if (!trainingAckDateSet) {
    payload.otherTrainingAcknowldegment = '';
  } else {
    payload.otherTrainingAcknowldegment = body.otherTrainingAcknowldegment
      ? DYSLEXIA_SCREENER_ANNUAL_ACKNOWLEDGEMENT_TEXT
      : '';
  }

  /** 1–3 = position within MDE window (not block index); do not trust stale form/API value. */
  payload.trainingCycle = returnDyslexiaScreenerTrainingYearOrdinalInBlock(
    body.schoolYear
  );

  delete payload.grade1FailedOrAtRiskPercentage;
  delete payload.kgFailedOrAtRiskPercentage;
  delete payload.kgExists;
  delete payload.grade1Exists;
  delete payload.grade1IEPStudentCount;
  delete payload.grade1504StudentCount;
  delete payload.kgIEPStudentCount;
  delete payload.kg504StudentCount;
  return payload as Omit<
    DyslexiaScreenerModel,
    | 'grade1FailedOrAtRiskPercentage'
    | 'kgFailedOrAtRiskPercentage'
    | 'kgExists'
    | 'grade1Exists'
    | 'grade1IEPStudentCount'
    | 'grade1504StudentCount'
    | 'kgIEPStudentCount'
    | 'kg504StudentCount'
  >;
};

export const createDyslexiaScreener = async (
  body: DyslexiaScreenerModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await DyslexiaScreenerServiceClient(config).post(
      dyslexiaScreenerApiPaths.create,
      stripClientOnlyDyslexiaScreenerFields(body)
    );
  });
};

export const updateDyslexiaScreener = async (
  body: DyslexiaScreenerModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest<unknown>(async () => {
    return await DyslexiaScreenerServiceClient(config).put(
      dyslexiaScreenerApiPaths.update,
      stripClientOnlyDyslexiaScreenerFields(body)
    );
  });
};

export const getDyslexiaScreenerListItems = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<DyslexiaScreenerListResponseModel>> => {
  return executeApiRequest<DyslexiaScreenerListResponseModel>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getListItems(schoolYear)
    );
  });
};

export const getDyslexiaScreenerContact = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<DyslexiaScreenerContactModel>> => {
  return executeApiRequest<DyslexiaScreenerContactModel>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getContact
    );
  });
};

export const postDyslexiaScreenerContact = async (
  contact: DyslexiaScreenerContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await DyslexiaScreenerServiceClient(config).post(
      dyslexiaScreenerApiPaths.createContact,
      contact
    );
  });
};

export const putDyslexiaScreenerContact = async (
  contact: DyslexiaScreenerContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await DyslexiaScreenerServiceClient(config).put(
      dyslexiaScreenerApiPaths.updateContact,
      contact
    );
  });
};

export const getDyslexiaScreenerSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getSchoolYears
    );
  });
};

export const getDyslexiaScreenerLeas = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getLeas
    );
  });
};

export const getDyslexiaScreenerSchoolsByLea = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getSchoolsByLeaId(leaId)
    );
  });
};

export const uploadDyslexiaScreenerFile = async (
  schoolYear: string,
  schoolIdentifier: string,
  leaIdentifier: string,
  file: File,
  documentType: string,
  config?: DataRequestConfig & {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }
): Promise<ApiResponseModel<{ id: string }>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('DocumentType', documentType);

  return executeApiRequest<{ id: string }>(async () => {
    return await DyslexiaScreenerServiceClient(config).upload(
      dyslexiaScreenerApiPaths.uploadFile(
        schoolYear,
        schoolIdentifier,
        leaIdentifier,
        documentType
      ),
      formData,
      config?.onUploadProgress
    );
  });
};

export const deleteDyslexiaScreenerFile = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await DyslexiaScreenerServiceClient(config).delete(
      dyslexiaScreenerApiPaths.deleteFile(leaId, id)
    );
  });
};

export const getDyslexiaScreenerFilesBySchool = async (
  schoolYear: string,
  leaId: string,
  schoolIdentifier: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<DyslexiaScreenerFileListItemModel[]>> => {
  return executeApiRequest<DyslexiaScreenerFileListItemModel[]>(async () => {
    return await DyslexiaScreenerServiceClient(config).get(
      dyslexiaScreenerApiPaths.getFilesBySchool(
        schoolYear,
        leaId,
        schoolIdentifier
      )
    );
  });
};

export const downloadDyslexiaScreenerFile = async (
  leaId: string,
  metaId: string,
  config?: DataRequestConfig
): Promise<Blob> => {
  const response = await DyslexiaScreenerServiceClient(config).getFile<Blob>(
    dyslexiaScreenerApiPaths.downloadFile(leaId, metaId)
  );
  return response.data;
};
